import { Injectable, inject } from '@angular/core';
import { Md5 } from 'ts-md5';
import { downloadFile } from '../../functions/download-file';
import { ClaimKey } from '../../models/api/core/claim-key';
import {
  CreateDraftNote,
  NoteTypeInput,
  SubTypesInput,
} from '../../models/api/view/claim/documentation/notes/notes-models';
import {
  NotesResponse,
  NoteTypesResponse,
  SubTypesResponse,
} from '../../models/api/view/claim/documentation/notes/notes-response-models';
import { NotesSearchInput } from '../../models/api/view/claim/documentation/notes/notes-search-input';
import { Api } from '../api/api.service';
import { BaseService } from '../base.service';
import { Session } from '../session/session.service';

@Injectable({ providedIn: 'root' })
export class NotesService extends BaseService {
  private api = inject(Api);
  private session = inject(Session);

  private notes = new Map<string, any>();

  constructor() {
    super();
    this.dispose.subs = this.session.cacheCleared.subscribe(() => {
      this.notes.clear();
    });
  }

  public getNoteItem(key: ClaimKey) {
    const cacheKey = `notes-${key.recordSource}-${key.recordId}`;
    if (!this.notes.has(cacheKey)) {
      this.notes.set(cacheKey, new NoteItem(key, this.api));
    }

    return this.notes.get(cacheKey);
  }
}

export class NoteItem {
  private notes: NotesResponse;
  private notesPreview: NotesResponse;
  private noteTypes: NoteTypesResponse;
  private subTypes: SubTypesResponse;
  private creationNoteTypes: NoteTypesResponse;
  private selectedDraft: any;
  private noteInputKey: string;

  constructor(
    public key: ClaimKey,
    public api: Api,
  ) {}

  async createNote(note: CreateDraftNote) {
    this.clearNotes();
    return await this.api.notes.createNote(note);
  }

  async upsertDraft(note: CreateDraftNote) {
    this.clearNotes();
    await this.api.notes.upsertDraft(note);
  }

  async deleteDraft(note: CreateDraftNote) {
    this.clearNotes();
    this.selectedDraft = null;
    await this.api.notes.deleteDraft(note);
  }

  async getNotes(input: NotesSearchInput): Promise<NotesResponse> {
    // if the note input changes, invalidate cache.
    const noteInputKey = Md5.hashStr(JSON.stringify(input));
    if (noteInputKey !== this.noteInputKey) {
      this.notes = null;
    }

    this.noteInputKey = noteInputKey;
    return (this.notes ??= await this.api.notes.getNotes(input));
  }

  clearNotes() {
    this.notes = null;
  }

  /**
   * Returns top 20 most recent notes.
   * @param input
   * @returns
   */
  async getNotesPreview(claimNumber: string): Promise<NotesResponse> {
    return (this.notesPreview ??= await this.api.notes.getNotes({
      searchNotes: {
        rowStart: 1,
        rowCount: 20,
        sortBy: 'date_create',
        sortDirection: 'DESC',
        includeFilters: false,
        includeDrafts: false,
        initialLoad: false,
        isExport: false,
        text: '',
        exportType: '',
        level: 'Claim',
        claimUID: this.key.recordId,
        fileNumber: claimNumber,
        absenceUID: claimNumber,
        accommodationRequestID: this.key.recordId,
        leaveNumber: this.key.recordId,
      },
      key: this.key,
    }));
  }

  async getNoteTypes(input: NoteTypeInput): Promise<NoteTypesResponse> {
    return (this.noteTypes ??= await this.api.notes.getNoteTypes(input));
  }

  async getSubTypes(input: SubTypesInput): Promise<SubTypesResponse> {
    return (this.subTypes ??= await this.api.notes.getSubTypes(input));
  }

  async getCreationNoteTypes(input: NoteTypeInput): Promise<NoteTypesResponse> {
    return (this.creationNoteTypes ??=
      await this.api.notes.getCreationNoteTypes(input));
  }

  async exportPDF(input: NotesSearchInput, filename: string): Promise<void> {
    const blob = await this.api.notes.exportPDF(input);
    downloadFile(blob, filename, blob.type);
  }

  setSelectedDraft(draft: any) {
    this.selectedDraft = draft;
  }

  getSelectedDraft() {
    return this.selectedDraft;
  }
}
